/* ============================================================
   Live Stripe wiring for the Model B card-capture. The backend
   (/api/billing/trial/setup-intent) decides what's needed:
     - 'active'       → org already has a live subscription → proceed
     - 'card_on_file' → a card is already saved → activate with it (no form)
     - 'collect'      → no card yet → mount <PaymentElement> + confirmSetup
   Then /api/billing/trial/activate creates the trialing subscription. The card
   is only mandatory because billing_status flips active ONLY after activation.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, LinkAuthenticationElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import TrialCardCapture from './TrialCardCapture.jsx';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { isChatGptOrigin } from '../lib/signupSource.js';
import { startCheckout } from '../lib/plans.js';
import { taxStripeKeyMatches, useTrialTax } from '../components/billing/useTrialTax.js';

// Publishable key is PUBLIC by design — it ships in the client bundle. Prefer the
// Pages env var; fall back to the live key so the card form works without a
// rebuild. loadStripe is memoised at module scope.
const PUBLISHABLE_KEY =
  import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY ||
  'pk_live_51PvPxwP9gu3OEw79CpTcfVfi1Iwj5F68GJMSNy4W88KQMfT80JEwGecNWwDXvb7Q3NRpZN5d8mtyl917hWPDLDSb00B8Vuic7O';
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

function brandLabel(brand, last4) {
  const b = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
  return last4 ? `${b} •••• ${last4}` : b;
}

// 'collect' — PaymentElement + confirmSetup → activate by SetupIntent id.
// `email` (the user's registration email) is handed to the PaymentElement so
// Stripe Link recognizes a returning user and offers their saved cards — the user
// can still switch to a different email / card inside the element.
function CollectForm({ clientSecret, email, onComplete, onCardSaved, planKey, selectedPlanKey, onSelectPlan, chatGptOrigin, automaticTax }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const tax = useTrialTax({ automaticTax: planKey === 'posting' ? null : automaticTax, planKey, disabled: submitting });

  async function handleSubmit() {
    if (!stripe || !elements || submitting || (planKey !== 'posting' && tax.busy)) return;
    setSubmitting(true);
    setError(null);
    let intent = null;
    try {
      if (planKey === 'posting') {
        await startCheckout('posting');
        return;
      }
      // If a prior attempt already saved this intent, don't re-confirm (a
      // succeeded intent can't be confirmed twice) — go straight to activation.
      if (clientSecret) {
        const r = await stripe.retrieveSetupIntent(clientSecret).catch(() => null);
        if (r?.setupIntent?.status === 'succeeded') intent = r.setupIntent;
      }
      if (!intent) {
        const { error: confirmErr, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
        if (confirmErr) {
          setError(confirmErr.message || t('trial.cardError', 'We could not confirm your card. Please try again.'));
          setSubmitting(false);
          return;
        }
        if (setupIntent?.status !== 'succeeded') {
          setError(t('trial.cardIncomplete', 'Card confirmation did not complete. Please try again.'));
          setSubmitting(false);
          return;
        }
        intent = setupIntent;
      }
      // planKey = the tier chosen in the picker (undefined pre-flip → backend
      // resolves the default plan; §5). The Stripe confirmSetup → activate flow
      // is otherwise byte-identical.
      await api.billing.activateTrial({ setupIntentId: intent.id, planKey, ...tax.activation });
      onComplete?.();
    } catch (err) {
      if (automaticTax && intent?.status === 'succeeded') {
        // A completed SetupIntent cannot represent newly edited card fields.
        // Reuse the existing saved-card view; its normal escape hatch starts a
        // fresh SetupIntent. Server verifies ownership again on every request.
        const saved = err?.details?.paymentMethod;
        const id = saved?.id || (typeof intent.payment_method === 'string' ? intent.payment_method : intent.payment_method?.id);
        if (id) {
          onCardSaved?.({ id, cardBrand: saved?.cardBrand ?? null, cardLast4: saved?.cardLast4 ?? null }, err?.details?.taxQuote ?? null, err?.message);
          return;
        }
      }
      setError(err?.message || t('trial.somethingWrong', 'Something went wrong. Please try again.'));
      setSubmitting(false);
    }
  }

  return (
    <TrialCardCapture
      automaticTax={automaticTax}
      taxQuote={tax.quote}
      stripeSlot={(
        <>
          {/* §5.5: pre-fill the authenticated user's email so Stripe Link
              recognizes THEM (offers their Link cards, or a clean form). This is
              a prefill hint, not a hard sign-out of a foreign Link session the
              browser may remember. Money path unchanged (the SetupIntent +
              customer are server-side; this email already = the customer's). */}
          <LinkAuthenticationElement options={email ? { defaultValues: { email } } : undefined} />
          <PaymentElement
            options={{ ...(email ? { defaultValues: { billingDetails: { email } } } : {}), ...(automaticTax ? { readOnly: submitting } : {}) }}
            onReady={() => setElementReady(true)}
            onChange={tax.onPaymentChange}
          />
        </>
      )}
      submitting={submitting}
      controlsLocked={tax.busy}
      canSubmit={elementReady && !!stripe && !!elements && (planKey === 'posting' || !tax.busy)}
      error={error || tax.error}
      onSubmit={handleSubmit}
      selectedPlanKey={selectedPlanKey}
      onSelectPlan={(key) => { if (!submitting && !tax.busy) onSelectPlan?.(key); }}
      chatGptOrigin={chatGptOrigin}
    />
  );
}

// 'card_on_file' — a card is already saved; activate the trial with it.
function CardOnFileForm({ paymentMethodId, cardBrand, cardLast4, onComplete, onUseDifferentCard, planKey, selectedPlanKey, onSelectPlan, chatGptOrigin, automaticTax, initialQuote, initialError }) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(initialError || null);
  const tax = useTrialTax({ automaticTax: planKey === 'posting' ? null : automaticTax, planKey, paymentMethodId, initialQuote, disabled: submitting });

  async function handleSubmit() {
    if (submitting || (planKey !== 'posting' && tax.busy)) return;
    setSubmitting(true);
    setError(null);
    try {
      if (planKey === 'posting') {
        await startCheckout('posting');
        return;
      }
      await api.billing.activateTrial({ paymentMethodId, planKey, ...tax.activation });
      onComplete?.();
    } catch (err) {
      if (automaticTax) tax.updateQuote(err?.details?.taxQuote);
      setError(err?.message || t('trial.somethingWrong', 'Something went wrong. Please try again.'));
      setSubmitting(false);
    }
  }

  // §7.2: a Stripe Link PM has no card object → cardBrand/cardLast4 are both
  // null, and brandLabel(null, null) returns the bare "Card" → "Card on file:
  // Card" reads broken. When there are no card details, show an honest distinct
  // sentence instead of interpolating a brand that doesn't exist.
  const hasCardDetails = !!(cardBrand || cardLast4);
  const slot = (
    <div style={{ padding: '8px 2px', fontSize: 14, lineHeight: 1.6 }}>
      <div>
        {hasCardDetails
          ? <>{t('trial.cardOnFile', 'Card on file')}: <b>{brandLabel(cardBrand, cardLast4)}</b></>
          : t('trial.savedPaymentMethod', 'You have a saved payment method')}
      </div>
      <button
        type="button"
        disabled={submitting || tax.busy}
        onClick={() => { if (!submitting && !tax.busy) onUseDifferentCard?.(); }}
        style={{ marginTop: 6, background: 'none', border: 0, padding: 0, color: 'var(--accent, #b8862f)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
      >
        {t('trial.useDifferentCard', 'Use a different card')}
      </button>
    </div>
  );

  return (
    <TrialCardCapture
      automaticTax={automaticTax}
      taxQuote={tax.quote}
      stripeSlot={slot}
      submitting={submitting}
      controlsLocked={tax.busy}
      canSubmit={!submitting && (planKey === 'posting' || !tax.busy)}
      error={error || tax.error}
      onSubmit={handleSubmit}
      selectedPlanKey={selectedPlanKey}
      onSelectPlan={(key) => { if (!submitting && !tax.busy) onSelectPlan?.(key); }}
      chatGptOrigin={chatGptOrigin}
    />
  );
}

export default function TrialCardCaptureContainer({ onComplete }) {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const { pending } = usePendingOAuth();
  const userEmail = user?.email || null;
  const [setup, setSetup] = useState(null);
  const [initError, setInitError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [forceCollect, setForceCollect] = useState(false);

  // Onboarding tier-picker (docs/onboarding-tariff-select-design-brief.md §4.3).
  // The selected planKey lives HERE, ABOVE <Elements>, so switching tiers
  // re-renders the picker + plan summary WITHOUT unmounting <Elements> /
  // <PaymentElement> — a half-entered card / Link session survives selection.
  const { newGridActive, postingPlanActive, ready } = usePlanCatalog();
  const chatGptOrigin = isChatGptOrigin(pending, user);
  // LATCH the grid choice (mirror of TrialCardCapture's gridChoiceRef, §6):
  // settle once the catalog is ready, never flip mid-flow, so the "send planKey
  // to the money path?" decision can't tear. Pre-flip → false → no planKey sent
  // → legacy single-plan trial resolves server-side exactly as today.
  const gridChoiceRef = useRef(null);
  if (gridChoiceRef.current === null && ready) gridChoiceRef.current = !!newGridActive;
  const useNewGrid = gridChoiceRef.current !== null ? gridChoiceRef.current : !!newGridActive;
  // 'growth' is the featured/recommended default the picker pre-selects.
  const [selectedPlanKey, setSelectedPlanKey] = useState('growth');
  useEffect(() => {
    if (useNewGrid && postingPlanActive && chatGptOrigin) setSelectedPlanKey('posting');
  }, [useNewGrid, postingPlanActive, chatGptOrigin]);
  // Only send a planKey to activateTrial when the picker is actually live;
  // undefined pre-flip keeps the legacy money path byte-identical.
  const planKeyForActivate = useNewGrid ? selectedPlanKey : undefined;

  useEffect(() => {
    let cancelled = false;
    if (!stripePromise) {
      setInitError(t('trial.notConfigured', 'Payments are not configured. Please contact support.'));
      return;
    }
    setInitError(null);
    setSetup(null);
    api.billing
      .createTrialSetupIntent({ forceCollect })
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 'active') { onComplete?.(); return; }
        setSetup(res || null);
      })
      .catch((err) => {
        if (!cancelled) setInitError(err?.message || t('trial.couldNotStart', 'Could not start your trial. Please try again.'));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, forceCollect]);

  const appearance = useMemo(() => {
    const dark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
    return { theme: dark ? 'night' : 'stripe', variables: { borderRadius: '10px' } };
  }, []);

  // <Elements> options are MEMOISED on [clientSecret, appearance] ONLY — never
  // on the selected plan. Selecting a tier re-renders the container but this
  // object reference stays stable, so react-stripe-js sees identical props and
  // the Payment Element iframe is never re-initialised (§4.3 / money-path
  // byte-identity). clientSecret is the single SetupIntent secret from step 1.
  const elementsOptions = useMemo(
    () => ({
      clientSecret: setup?.clientSecret,
      // §5.5: bind the Element to the server-created CustomerSession so the
      // AUTHENTICATED customer's own saved cards are shown (scoped to their
      // Stripe Customer). Stable per SetupIntent fetch (same `setup` object), so
      // the §4.3 no-remount invariant holds. (This scopes SAVED-PM display, not
      // the Link-wallet browser session — that's a soft prefill via the
      // LinkAuthenticationElement email below, not a hard eviction.)
      ...(setup?.customerSessionClientSecret
        ? { customerSessionClientSecret: setup.customerSessionClientSecret }
        : {}),
      appearance,
    }),
    [setup?.clientSecret, setup?.customerSessionClientSecret, appearance],
  );

  if (initError) {
    return (
      <TrialCardCapture
        stripeSlot={null}
        error={initError}
        canSubmit={!!stripePromise}
        onSubmit={() => setReloadKey((k) => k + 1)}
        selectedPlanKey={selectedPlanKey}
        onSelectPlan={setSelectedPlanKey}
        chatGptOrigin={chatGptOrigin}
      />
    );
  }

  // Loading the setup decision, or 'active' (onComplete already fired) → skeleton.
  if (!setup) {
    return <TrialCardCapture stripeSlot={null} selectedPlanKey={selectedPlanKey} onSelectPlan={setSelectedPlanKey} chatGptOrigin={chatGptOrigin} />;
  }

  if (setup.status === 'card_on_file') {
    if (!taxStripeKeyMatches(PUBLISHABLE_KEY, setup.automaticTax)) return <TrialCardCapture canSubmit={false} error={t('trial.testKeyMismatch', 'Billing is not configured for this environment. Please contact support.')} />;
    const savedCardForm = (
      <CardOnFileForm
        automaticTax={setup.automaticTax}
        paymentMethodId={setup.paymentMethodId}
        cardBrand={setup.cardBrand}
        cardLast4={setup.cardLast4}
        initialQuote={setup.reviewPlanKey === planKeyForActivate ? setup.taxQuote : null}
        initialError={setup.reviewError}
        onComplete={onComplete}
        onUseDifferentCard={() => { setSetup(null); setForceCollect(true); setReloadKey(key => key + 1); }}
        planKey={planKeyForActivate}
        selectedPlanKey={selectedPlanKey}
        onSelectPlan={setSelectedPlanKey}
        chatGptOrigin={chatGptOrigin}
      />
    );
    return savedCardForm;
  }

  // status === 'collect'. NOTE: no key={} on <Elements>, options memoised on
  // clientSecret only — selecting a tier never remounts the Payment Element.
  if (!taxStripeKeyMatches(PUBLISHABLE_KEY, setup.automaticTax)) return <TrialCardCapture canSubmit={false} error={t('trial.testKeyMismatch', 'Billing is not configured for this environment. Please contact support.')} />;
  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <CollectForm
        automaticTax={setup.automaticTax}
        clientSecret={setup.clientSecret}
        email={userEmail}
        onComplete={onComplete}
        onCardSaved={(card, taxQuote, reviewError) => setSetup({
          status: 'card_on_file', paymentMethodId: card.id, cardBrand: card.cardBrand,
          cardLast4: card.cardLast4, automaticTax: setup.automaticTax, taxQuote,
          reviewError, reviewPlanKey: planKeyForActivate,
        })}
        planKey={planKeyForActivate}
        selectedPlanKey={selectedPlanKey}
        onSelectPlan={setSelectedPlanKey}
        chatGptOrigin={chatGptOrigin}
      />
    </Elements>
  );
}
